import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { useFormikContext } from 'formik';

import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import Divider from 'core/components/divider';
import { Label } from 'core/components/label';
import { Loading } from 'core/components/loading';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';
import { awsDateToDateObject, nthDayFormatter, numberFormatter } from 'core/helpers/formatters';
import { InlineCallout } from 'quote-revisit/components/inline-callout';
import { isPaymentScheduleOutOfDate } from 'quote-revisit/helpers/payment-schedule-out-of-date';
import { amountOf } from 'quote-revisit/modify-quote/left-column/policy-cards/price-details/get-price-details';
import { BillingDayMode, getBillingDay, getDefaultBillingDay, LAST_BILLING_DAY } from './billing-day';
import BillingDayControls, { BillingDayChoice } from './billing-day-controls';
import {
  BILLING_DAY_CHANGED,
  CLOSE_PAYMENTS_RESOLUTION_SELECTED,
  getBillingDayChangedProperties,
  getCloseResolutionProperties
} from './billing-day-events';
import { usePaymentSchedulePreview } from './payment-schedule-preview';
import {
  getPaymentScheduleViewedProperties,
  PAYMENT_SCHEDULE_VIEWED,
  PaymentScheduleSource,
  policyTypeByLine
} from './payment-schedule-viewed';
import { CloseResolution, getDaysBetweenFirstTwoPayments, getSpacingBucket, SpacingBucket } from './payment-spacing';
import rejectionIcon from './ic-rejection-16px.svg';
import useStyles from './payment-schedule-modal.styles';

const PREVIEW_FAILED_MESSAGE =
  'We couldn’t load the payment dates for this billing day. Select Done to try again, or use the effective date.';

const formatAmount = (amount) => `$${numberFormatter(amount, 2)}`;

const BillingDayCallout = ({ billingDay }) =>
  billingDay.mode === BillingDayMode.Custom ? (
    // Neutral copy: the mode is derived, so `custom` can't tell an agent-picked day from one nobody picked.
    <InlineCallout variant="light">{`Payments are charged on the ${nthDayFormatter(billingDay.day)}.`}</InlineCallout>
  ) : (
    <InlineCallout variant="light">
      {`The billing day follows the policy’s effective date: the ${nthDayFormatter(billingDay.day)}.`}
    </InlineCallout>
  );

BillingDayCallout.propTypes = {
  billingDay: PropTypes.shape({ day: PropTypes.number.isRequired, mode: PropTypes.string.isRequired }).isRequired
};

const ScheduleTable = ({ schedule }) => {
  const classes = useStyles();
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th scope="col">Date due</th>
          <th scope="col">Payment number</th>
          <th scope="col" className={classes.amount}>
            Amount
          </th>
        </tr>
      </thead>
      <tbody>
        {schedule.map((payment, index) => (
          <tr key={payment.date}>
            <td>
              {format(awsDateToDateObject(payment.date), 'MMM d, yyyy')}
              {index === 0 && <span className={classes.dueAtBind}>(Due at bind)</span>}
            </td>
            <td>{`Payment ${index + 1} of ${schedule.length}`}</td>
            <td className={classes.amount}>{formatAmount(amountOf(payment))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

ScheduleTable.propTypes = {
  schedule: PropTypes.arrayOf(PropTypes.shape({ date: PropTypes.string.isRequired })).isRequired
};

const ScheduleSection = ({ schedule, total, isOutOfDate, isLoading, hasError, children }) => {
  const classes = useStyles();
  return (
    <section className={classes.section} aria-labelledby="payment-schedule-dates">
      <Label type="body1Bold" component="h3" id="payment-schedule-dates" className={classes.heading}>
        Payment dates
      </Label>
      {isOutOfDate && (
        <InlineCallout variant="light" icon={rejectionIcon}>
          Update the quote to see payment dates and amounts.
        </InlineCallout>
      )}
      {children}
      {!isOutOfDate && isLoading && <Loading noBackground className={classes.loading} />}
      {!isOutOfDate && !isLoading && hasError && (
        <InlineCallout variant="light" icon={rejectionIcon}>
          {PREVIEW_FAILED_MESSAGE}
        </InlineCallout>
      )}
      {!isOutOfDate && !isLoading && !hasError && (
        <>
          <ScheduleTable schedule={schedule} />
          <div className={classes.termTotal}>
            <Divider className={classes.divider} />
            <div className={classes.termTotalRow}>
              <Label type="body3" component="span" className={classes.termTotalLabel}>
                Total term price
              </Label>
              <Label type="body2" component="span" className={classes.termTotalValue}>
                {formatAmount(total)}
              </Label>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

ScheduleSection.propTypes = {
  schedule: PropTypes.array.isRequired,
  total: PropTypes.number,
  isOutOfDate: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  children: PropTypes.node
};

ScheduleSection.defaultProps = {
  total: null,
  isLoading: false,
  hasError: false,
  children: null
};

const CappedBillingDayCallout = ({ effectiveDay }) => (
  <InlineCallout variant="light">
    {`This policy’s effective date is on the ${nthDayFormatter(effectiveDay)}. Future payments will be charged on the ${nthDayFormatter(LAST_BILLING_DAY)}, since not every month has a ${nthDayFormatter(effectiveDay)}.`}
  </InlineCallout>
);

CappedBillingDayCallout.propTypes = {
  effectiveDay: PropTypes.number.isRequired
};

const ReadOnlyPaymentSchedule = ({ policy }) => {
  const schedule = policy.schedule ?? [];
  const billingDay = getBillingDay(policy);
  return (
    <>
      {billingDay && <BillingDayCallout billingDay={billingDay} />}
      <ScheduleSection schedule={schedule} total={policy.total} isOutOfDate={false}>
        {billingDay?.mode === BillingDayMode.Capped28 && (
          <CappedBillingDayCallout effectiveDay={billingDay.effectiveDay} />
        )}
      </ScheduleSection>
    </>
  );
};

ReadOnlyPaymentSchedule.propTypes = {
  policy: PropTypes.object.isRequired
};

/**
 * Modify Quote's billing day controls and the schedule they produce (#36361). The agent's changes stay
 * in this component until the modal closes; `saveRef` gets the function that validates them and writes
 * them to the offer form as pending changes, which brings up the update footer.
 *
 * The schedule is the offer's saved one while the billing day and combine choice match what was saved,
 * and the schedule preview (#36354) otherwise. The preview prices the offer as last saved, so it doesn't
 * see unsaved edits such as a new effective date.
 *
 * shortcut: "Use the effective date" sets the billing day to the effective day (the 28th for the
 * 29th–31st) and is read back by comparing the two days, because the offer has no stored "billing day
 * follows the effective date" flag yet. Set and read that flag once #35596 adds it.
 */
const EditablePaymentSchedule = ({ policy, saveRef, onCheckingChange }) => {
  const { offer: store } = useStore();
  const session = useSession();
  const toast = useToast();
  const { values, setFieldValue } = useFormikContext();
  const { fields } = policy;
  const formGlobal = values.global ?? {};

  const effectiveDate = formGlobal[fields.effectiveDate] ?? policy.effectiveDate;
  const effectiveDay = Number(effectiveDate?.slice(8, 10));
  const defaultDay = getDefaultBillingDay(effectiveDate);
  const savedBillingDay = getBillingDay(policy)?.day;
  const [initialDay] = useState(() => Number(formGlobal[fields.billingDayOfMonth]) || defaultDay);
  const [initialCombine] = useState(() => Boolean(formGlobal[fields.megaDownPay]));

  const [choice, setChoice] = useState(
    initialDay === defaultDay ? BillingDayChoice.EffectiveDate : BillingDayChoice.Custom
  );
  const [customDay, setCustomDay] = useState(initialDay);
  // Unanswered (null) only after the agent picks a new day: a saved day was already answered.
  const [resolution, setResolution] = useState(initialCombine ? CloseResolution.Combine : CloseResolution.KeepSeparate);
  const [showError, setShowError] = useState(false);

  const isCustom = choice === BillingDayChoice.Custom;
  const billingDay = isCustom ? customDay : defaultDay;
  const previewVariables = {
    offerId: store.offer?.id,
    option: policy.optionType,
    policyType: policyTypeByLine[policy.key]
  };

  // How far apart the day puts the first two payments when they're kept separate.
  const spacingPreview = usePaymentSchedulePreview({
    ...previewVariables,
    billingDayOfMonth: customDay,
    megaDownPay: false,
    skip: !isCustom
  });
  const daysApart = isCustom ? getDaysBetweenFirstTwoPayments(spacingPreview.preview?.schedule) : null;
  const spacingBucket = getSpacingBucket(daysApart);
  const spacing = spacingBucket && {
    days: daysApart,
    bucket: spacingBucket,
    paymentCount: spacingPreview.preview.schedule.length
  };
  const needsResolution = spacingBucket === SpacingBucket.Under15;
  const combine = needsResolution && resolution === CloseResolution.Combine;

  const isOutOfDate = isPaymentScheduleOutOfDate(policy.schedule ?? []);
  // Outside the under-15 choice the saved schedule is right for the saved day, combined or not.
  const isSaved = billingDay === savedBillingDay && (!needsResolution || combine === policy.megaDownPay);
  const isAwaitingResolution = needsResolution && !resolution;
  const schedulePreview = usePaymentSchedulePreview({
    ...previewVariables,
    billingDayOfMonth: billingDay,
    megaDownPay: combine,
    skip: isSaved || isOutOfDate || isAwaitingResolution
  });

  const isCheckingSpacing = isCustom && spacingPreview.loading;
  // No spacing preview (an error, or a null answer) means a custom day's spacing is unknown.
  const isSpacingUnknown = isCustom && !spacingPreview.loading && !spacingPreview.preview;
  const hasPreviewError = !isSaved && Boolean(schedulePreview.error || isSpacingUnknown);
  useEffect(() => {
    onCheckingChange(isCheckingSpacing);
  }, [isCheckingSpacing]);

  saveRef.current = () => {
    if (isCheckingSpacing) {
      return false;
    }
    // A new day whose payments couldn't be previewed might need the keep separate or combine answer.
    if (isCustom && hasPreviewError && billingDay !== initialDay) {
      toast.notify({
        type: 'error',
        message: 'We couldn’t load the payment dates for this billing day. Try again, or use the effective date.',
        durationInSeconds: 3
      });
      spacingPreview.refetch().catch(() => {});
      schedulePreview.refetch().catch(() => {});
      return false;
    }
    if (isAwaitingResolution) {
      setShowError(true);
      toast.notify({
        type: 'error',
        message: 'Please fix the errors in the form before continuing',
        durationInSeconds: 3
      });
      return false;
    }
    if (billingDay !== initialDay || (needsResolution && combine !== initialCombine)) {
      setFieldValue(`global.${fields.billingDayOfMonth}`, billingDay);
      // null, not false, like setMegaDownPayOnOffer: the field is unset unless the agent combines.
      setFieldValue(`global.${fields.megaDownPay}`, combine || null);
    }
    if (billingDay !== initialDay && billingDay !== defaultDay) {
      track(
        BILLING_DAY_CHANGED,
        getBillingDayChangedProperties({
          policy,
          offerId: store.offer?.id,
          previousBillingDay: initialDay,
          newBillingDay: billingDay,
          daysBetweenFirstTwoPayments: daysApart,
          state: store.state,
          session
        })
      );
    }
    return true;
  };

  const handleCustomDayChange = (day) => {
    setCustomDay(day);
    setResolution(null);
    setShowError(false);
  };

  const handleResolutionChange = (selectedResolution) => {
    setResolution(selectedResolution);
    setShowError(false);
    track(
      CLOSE_PAYMENTS_RESOLUTION_SELECTED,
      getCloseResolutionProperties({
        policy,
        offerId: store.offer?.id,
        resolution: selectedResolution,
        daysApart,
        session
      })
    );
  };

  const schedule = isSaved ? (policy.schedule ?? []) : (schedulePreview.preview?.schedule ?? []);
  const total = isSaved ? policy.total : (schedulePreview.preview?.termTotal ?? null);

  return (
    <>
      {defaultDay && (
        <BillingDayControls
          effectiveDay={effectiveDay}
          choice={choice}
          onChoiceChange={setChoice}
          customDay={customDay}
          onCustomDayChange={handleCustomDayChange}
          spacing={spacing}
          // The price only holds if Update quote keeps the rate control date.
          termTotal={spacingPreview.preview?.rateControlDateChangesOnUpdate ? null : spacingPreview.preview?.termTotal}
          resolution={resolution}
          onResolutionChange={handleResolutionChange}
          showError={showError}
        />
      )}
      {!isAwaitingResolution && (
        <ScheduleSection
          schedule={schedule}
          total={total}
          isOutOfDate={isOutOfDate}
          isLoading={!isSaved && (schedulePreview.loading || (isCustom && spacingPreview.loading))}
          hasError={hasPreviewError}
        >
          {!isCustom && effectiveDay > LAST_BILLING_DAY && <CappedBillingDayCallout effectiveDay={effectiveDay} />}
        </ScheduleSection>
      )}
    </>
  );
};

EditablePaymentSchedule.propTypes = {
  policy: PropTypes.shape({
    key: PropTypes.string.isRequired,
    optionType: PropTypes.string,
    fields: PropTypes.object.isRequired,
    total: PropTypes.number,
    schedule: PropTypes.array,
    megaDownPay: PropTypes.bool,
    effectiveDate: PropTypes.string,
    billingDayOfMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  saveRef: PropTypes.shape({ current: PropTypes.func }).isRequired,
  onCheckingChange: PropTypes.func.isRequired
};

/**
 * A policy's payment schedule (#36360): every payment's date, number and amount, and the term total.
 * Opens from the Modify Quote policy card, and read-only on Checkout (#36363).
 *
 * On Modify Quote it also holds the billing day controls (#36361), and closing it (Done, the close button
 * or Escape) saves them as pending changes, unless a day under 15 days from bind still needs a keep
 * separate or combine answer. A schedule rated before today is out of date there (see
 * isPaymentScheduleOutOfDate), so it asks for a quote update instead of listing stale dates. Checkout
 * never shows that: an offer can only bind on the day it was rated.
 */
const PaymentScheduleModal = ({ open, onClose, policy, readOnly }) => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const session = useSession();
  const saveRef = useRef(null);
  const [isCheckingSpacing, setIsCheckingSpacing] = useState(false);
  const source = readOnly ? PaymentScheduleSource.Checkout : PaymentScheduleSource.ModifyQuote;

  // Once per open, not on every re-render of the page behind it.
  useEffect(() => {
    if (open) {
      track(
        PAYMENT_SCHEDULE_VIEWED,
        getPaymentScheduleViewedProperties({
          policy,
          offerId: store.offer?.id,
          selectedOption: store.selectedOption,
          state: store.state,
          source,
          session
        })
      );
    }
  }, [open]);

  const handleClose = () => {
    if (readOnly || saveRef.current?.()) {
      onClose();
    }
  };

  return (
    <BaseDialog open={open} onClose={handleClose} size="md" fullWidth className={classes.dialog}>
      <div className={classes.content}>
        <Label type="h1" component="h2" className={classes.title}>
          Payment schedule
        </Label>
        {readOnly ? (
          <ReadOnlyPaymentSchedule policy={policy} />
        ) : (
          <EditablePaymentSchedule policy={policy} saveRef={saveRef} onCheckingChange={setIsCheckingSpacing} />
        )}
        <div className={classes.actions}>
          <Button variant="contained" color="primary" onClick={handleClose} disabled={isCheckingSpacing}>
            Done
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

PaymentScheduleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  policy: PropTypes.shape({
    key: PropTypes.string.isRequired,
    total: PropTypes.number,
    schedule: PropTypes.array,
    effectiveDate: PropTypes.string,
    billingDayOfMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  readOnly: PropTypes.bool
};

PaymentScheduleModal.defaultProps = {
  readOnly: false
};

export default PaymentScheduleModal;
